import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Check, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export const FeatureFlags = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [availableModules, setAvailableModules] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchData();
  }, [customerId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/superadmin/customers/${customerId}/modules`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setCustomerData({
        id: response.data.customer_id,
        name: response.data.customer_name
      });
      setAvailableModules(response.data.available_modules);
      setEnabledModules(response.data.enabled_modules);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
      toast.error('Fehler beim Laden der Module');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    setEnabledModules(prev => {
      const isEnabled = prev.includes(moduleId);
      if (isEnabled) {
        return prev.filter(id => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/api/superadmin/customers/${customerId}/modules`,
        { enabled_modules: enabledModules },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Module erfolgreich aktualisiert!');
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to update modules:', error);
      toast.error('Fehler beim Speichern der Module');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryModules = (category) => {
    return availableModules.filter(m => m.category === category);
  };

  const categories = [...new Set(availableModules.map(m => m.category))];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/superadmin/customers')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zur Kundenliste
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
                Feature-Flags
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Module für <span className="font-semibold">{customerData?.name}</span> verwalten
              </p>
            </div>
            
            {hasChanges && (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Änderungen speichern
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {availableModules.length}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Verfügbare Module
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {enabledModules.length}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Aktive Module
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-600 dark:text-gray-400">
                  {availableModules.length - enabledModules.length}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Deaktivierte Module
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modules by Category */}
        {categories.map(category => (
          <Card key={category}>
            <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900 dark:to-orange-800">
              <CardTitle className="text-lg dark:text-gray-100">
                {category}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4">
                {getCategoryModules(category).map(module => {
                  const isEnabled = enabledModules.includes(module.id);
                  
                  return (
                    <div
                      key={module.id}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 border-2 rounded-lg transition-all ${
                        isEnabled 
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-600' 
                          : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-start gap-3 flex-1 mb-3 sm:mb-0">
                        <div className="text-3xl flex-shrink-0">
                          {module.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-base dark:text-gray-100">
                              {module.name}
                            </h3>
                            {isEnabled ? (
                              <Badge className="bg-green-500 text-white text-xs">
                                <Check className="h-3 w-3 mr-1" />
                                Aktiv
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500 dark:text-gray-400 dark:border-gray-600 text-xs">
                                <X className="h-3 w-3 mr-1" />
                                Deaktiviert
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {module.description}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Modul-ID: <code className="bg-gray-200 dark:bg-gray-700 dark:text-gray-300 px-1 rounded">{module.id}</code>
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-end sm:justify-start">
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => toggleModule(module.id)}
                          className="data-[state=checked]:bg-green-500"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Info Box */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="text-2xl">💡</div>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900 mb-2">
                  Hinweise zur Modul-Verwaltung
                </h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Deaktivierte Module werden im Admin-Menü ausgeblendet</li>
                  <li>Bestehende Daten bleiben erhalten und werden nicht gelöscht</li>
                  <li>Module können jederzeit wieder aktiviert werden</li>
                  <li>Änderungen werden sofort nach dem Speichern wirksam</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
